import React from 'react';
import styled from 'styled-components';
import {
  Button,
  Icon,
  Modal,
  Upload,
  UploadFileType,
  FileList,
  message,
} from 'components';
import NeedContext, { FileType } from '../NeedContext';

type UploadFileModalProps = {
  visible: boolean;
  setVisible: (visible: boolean) => void;
};

type ExtendedFileType = FileType & {
  status: 'uploading' | 'error' | 'done';
  percent: number;
  size: number;
  filename: string;
  uid: string;
  mimetype: string;
};

const UploadFileModal: React.FC<UploadFileModalProps> = ({
  visible,
  setVisible,
}) => {
  const { saveFiles } = React.useContext(NeedContext);
  const [files, setFiles] = React.useState<ExtendedFileType[]>([]);

  const convertFile = (file: UploadFileType, fileList: UploadFileType[]) => {
    let fileStatus: 'uploading' | 'done' | 'error' = 'uploading';
    if (file.response && file.response.status === 'error') {
      fileStatus = 'error';
      const errorFile = fileList.find(({ uid }) => uid === file.uid);
      if (errorFile) errorFile.status = 'error';
      message.error(`Error uploading file: ${file.name}`);
    } else if (file.status === 'done' && file.response?.url) {
      fileStatus = 'done';
    }
    return {
      id: file.uid,
      name: file.name,
      filetype: file.type,
      uid: file.uid,
      filename: file.name,
      mimetype: file.type,
      url: file.response?.url,
      size: file.size,
      status: fileStatus,
      // updatedAt: file.lastModifiedDate,
      percent: file.percent,
    } as ExtendedFileType;
  };

  const loading = React.useMemo(() => files.some(file => file.status === 'uploading'), [files]);

  React.useEffect(() => {
    setFiles([]);
  }, [visible]);

  return (
    <Modal
      open={visible}
      onCancel={() => setVisible(false)}
      title="Upload Files"
      okButtonProps={{ loading, disabled: loading }}
      onOk={() => {
        setVisible(false);
        saveFiles(files);
      }}
    >
      <StyledContent>
        {files.length > 0 && (
          <FileList
            files={files}
            showPreview
            onRemoveFile={(file, index) =>
              setFiles(prev => prev.filter((f, i) => i !== index))
            }
          />
        )}
        <Upload
          description="You may upload single or multiple files at a time."
          onChange={({ file, fileList }) => {
            const newFile = { ...file };
            if (newFile?.response && newFile?.error?.status >= 400) {
              const errorDetails =
                newFile.response?.message || newFile.response || 'Upload Error';
              message.error(
                `Error uploading file: ${newFile.name}. ${errorDetails}`,
              );
              newFile.status = 'error';
            }

            setFiles(prev => {
              if (prev.find(prevFile => prevFile.id === file.uid)) {
                return prev.map(prevFile => {
                  if (prevFile.id === file.uid) {
                    return convertFile(file, fileList);
                  }
                  return prevFile;
                });
              }
              return [...prev, convertFile(file, fileList)];
            });
          }}
          showUploadList={false}
          action="/upload/single"
          multiple
        >
          <Button>
            <Icon type="antdUploadOutlined" /> Upload Files
          </Button>
        </Upload>
      </StyledContent>
    </Modal>
  );
};

const StyledContent = styled.div`
  width: 100%;
  text-align: center;
  ${FileList} {
    max-height: calc(100vh - 400px);
    overflow: auto;
    margin-bottom: 25px;
  }
`;

export default UploadFileModal;
